// CAudioManager — 訂閱 IEventBus，並提供 UI 動畫時序音效入口
// 對應 PRD §3.2.5 Audio、§11 音效規範
//
// 設計原則：
// - 核心事件音效訂閱 IEventBus 自動 play；視覺時序音效由 UI callback 觸發
// - 透過 TSettings 控制全域開關（audioOn / bgmOn）
// - 原版 APK 可取得的音訊由 CBootScene 統一載入；播放失敗時靜默略過
//
// 事件→音效對應（依原版 TexturePool / BasePlayerEntity）：
//   捨牌進入海底      → card_stack（由 GameScene 落地 callback 觸發；摸牌不播）
//   meld.formed       → 本次啟動選定角色的 chow / pong / kong 語音
//   tile.flower-replaced / round.draw / 開局 → 同一組 Amy／Cindy 開局語音
//   胡牌儀式印記      → 本次啟動選定角色的 win / win_hand 語音
//   tenpai.declared   → focus 提示音，並把牌桌 BGM 切成 special_bg01

import type Phaser from 'phaser';

import type { TRoundEventMap } from '@core/events';
import { RoundSummaryKind } from '@core/events';
import type { IEventBus } from '@event-bus/event-bus';
import { loadSettings, type TSettings } from '@storage/index';
import { MeldType } from '@core/meld';
import {
  AudioKey,
  BGMTrack,
  selectRandomAudioItem,
  START_VOICE_PACKS,
  StartVoiceCue,
  startVoiceAudioKey,
  VoiceCue,
  voiceAudioKey,
  VOICE_PACKS,
} from './audio-keys';
import { stopBackgroundMusic, syncBackgroundMusic } from './scene-audio';

const ACTIVE_VOICE_PACK = selectRandomAudioItem(VOICE_PACKS); // 原 APP 每次啟動隨機 10 選 1
const ACTIVE_START_VOICE_PACK = selectRandomAudioItem(START_VOICE_PACKS); // 開局語音另行 Amy／Cindy 2 選 1

export class CAudioManager {
  private settings: TSettings;
  private tenpaiBGMActive = false;

  constructor(
    private readonly scene: Phaser.Scene,
    bus: IEventBus<TRoundEventMap>
  ) {
    this.settings = loadSettings();

    bus.on('tile.flower-replaced', () =>
      this.play(startVoiceAudioKey(ACTIVE_START_VOICE_PACK, StartVoiceCue.Flower))
    );

    bus.on('meld.formed', (p) => {
      const cue =
        p.meld.type === MeldType.Chi
          ? VoiceCue.Chi
          : p.meld.type === MeldType.Pong
            ? VoiceCue.Pong
            : VoiceCue.Kong;
      this.play(voiceAudioKey(ACTIVE_VOICE_PACK, cue));
    });

    bus.on('round.ended', (p) => {
      if (p.summary.kind !== RoundSummaryKind.Hu) {
        this.play(startVoiceAudioKey(ACTIVE_START_VOICE_PACK, StartVoiceCue.Draw));
      }
    });

    bus.on('tenpai.declared', () => {
      this.play(AudioKey.Tenpai);
      this.tenpaiBGMActive = true;
      syncBackgroundMusic(this.scene, BGMTrack.Tenpai);
    });
    this.toggleBGM(this.settings.bgmOn);
  }

  /** 胡牌儀式印記出現的瞬間播放原版語音；由動畫元件的時序 callback 觸發。 */
  playWinCeremony(isSelfDraw: boolean): void {
    this.play(voiceAudioKey(ACTIVE_VOICE_PACK, isSelfDraw ? VoiceCue.SelfDraw : VoiceCue.Win));
  }

  /** 原 APP 每局進入 GameScene 時，播放本次啟動選定的 Amy／Cindy 開局語音。 */
  playGameStart(): void {
    this.play(startVoiceAudioKey(ACTIVE_START_VOICE_PACK, StartVoiceCue.Start));
  }

  /** 捨牌真正進入海底時播放牌聲；不可綁在較早發生的 tile.discarded 核心事件。 */
  playDiscardLanding(): void {
    this.play(AudioKey.TileStack);
  }

  /**
   * 播放某個 sound key（音效）。
   * - 全域音效開關關 → 略過
   * - sound 沒載入（asset 還沒備好）→ 靜默略過
   */
  private play(key: string): void {
    if (!this.settings.audioOn) return;
    if (!this.scene.cache.audio.exists(key)) return;
    try {
      this.scene.sound.play(key);
    } catch {
      // 個別播放失敗不應影響遊戲（例：autoplay policy 阻擋）
    }
  }

  /** 啟動 / 停止背景音樂。 */
  toggleBGM(on: boolean): void {
    this.settings = { ...this.settings, bgmOn: on };
    syncBackgroundMusic(this.scene, this.tenpaiBGMActive ? BGMTrack.Tenpai : BGMTrack.Table);
  }

  /** 原版暫停視窗按繼續時固定恢復牌桌音樂，不延續聽牌特殊音樂。 */
  resumeTableBGM(): void {
    this.tenpaiBGMActive = false;
    syncBackgroundMusic(this.scene, BGMTrack.Table);
  }

  /** 暫停視窗開啟時停止所有 BGM。 */
  pauseBGM(): void {
    stopBackgroundMusic(this.scene);
  }

  /** 同步外部 TSettings 改變（例：使用者改了音效開關）。 */
  refreshSettings(settings: TSettings): void {
    this.settings = settings;
    this.toggleBGM(settings.bgmOn);
  }

  /** 原版 GameScene 離場時停止音樂；結算畫面維持無 BGM。 */
  destroy(): void {
    stopBackgroundMusic(this.scene);
  }
}
